import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicPageShell from '../components/PublicPageShell';
import { cmsApi } from '../lib/api';
import {
  Handshake, Folder, Shield, Lightning, Scales, ChartLineUp,
  CurrencyDollar, Star, Quotes, CheckCircle, ArrowRight,
} from '@phosphor-icons/react';

const USE_CASES = [
  {
    key: 'mna',
    label: 'Mergers & Acquisitions',
    icon: Handshake,
    tint: '#002677',
    hook: 'Close deals 30% faster with AI-ready data rooms.',
    description:
      'Every signal from teaser to signing captured in one deal room. AI readiness scoring surfaces red flags before the buyer does.',
    bullets: [
      'Secure data rooms with granular access control',
      'AI Deal Readiness Scorer (GPT-5.2)',
      'Q&A workflows, NDA tracking, audit trail',
      'Integrated deal pipeline + contacts CRM',
    ],
  },
  {
    key: 'fundraising',
    label: 'Fundraising & Capital Raises',
    icon: ChartLineUp,
    tint: '#41B6E6',
    hook: 'Run a tight process with global investors, from first pitch to wire.',
    description:
      'Share teasers, IMs and MNPI-safe data rooms with hundreds of LPs without losing control. Track engagement by document and page.',
    bullets: [
      'Data room analytics (views, downloads, dwell time)',
      'Staged disclosure with watermarking',
      'Investor CRM + pipeline',
      'One-click termsheet generation',
    ],
  },
  {
    key: 'divestitures',
    label: 'Divestitures & Carve-Outs',
    icon: CurrencyDollar,
    tint: '#00B140',
    hook: 'Untangle entities, carve cleanly, maximise sale price.',
    description:
      'Map assets, contracts and people to the carve-out perimeter in days. Share clean data rooms per bidder.',
    bullets: [
      'Entity mapping + contract registry',
      'Per-bidder data rooms, redaction studio',
      'Transition Services Agreement tracker',
      'AI valuation estimator (beta)',
    ],
  },
  {
    key: 'procurement',
    label: 'Procurement Transformation',
    icon: Lightning,
    tint: '#6B7280',
    hook: 'Source-to-pay with live supplier risk & ESG scoring.',
    description:
      'Consolidate supplier data, run RFQs, and let AI surface savings opportunities across your tail spend.',
    bullets: [
      'Supplier 360 with risk + ESG',
      'RFQ and e-auction workflows',
      'Spend analytics & anomaly detection',
      'Contract lifecycle management',
    ],
  },
  {
    key: 'grc',
    label: 'Risk, Audit & Compliance',
    icon: Scales,
    tint: '#E4002B',
    hook: 'SOC 2, ISO 27001, GDPR — auditable in one command center.',
    description:
      'Evidence collection, control testing and regulator-ready reports from a single source of truth. No more spreadsheet ping-pong.',
    bullets: [
      'Control library with evidence vault',
      'Auto-reminders + owner accountability',
      'Regulator export (SOC 2 / ISO / GDPR)',
      'Immutable audit trail',
    ],
  },
  {
    key: 'board',
    label: 'Board & Committee Packs',
    icon: Folder,
    tint: '#8347AD',
    hook: 'Assemble, distribute and archive board packs in minutes.',
    description:
      'Replace email-attachment chaos with a secure, mobile-friendly board portal. AI drafts executive summaries from your raw docs.',
    bullets: [
      'Branded board binders',
      'Granular watermarking + expiry',
      'AI executive summaries',
      'Meeting minutes & voting',
    ],
  },
];

const DEFAULT_CASE_STUDIES = [
  {
    quote: 'We closed a $180M carve-out in 11 weeks. BIL cut our Q&A turnaround from days to hours.',
    author: 'Clara Jensen',
    role: 'Head of Corporate Development',
    company: 'Nordic Industries AG',
    stat: '11 weeks',
    stat_label: 'Time to signing',
  },
  {
    quote: 'The AI readiness scorer caught three data-quality issues before our lead investor did. That alone paid for the year.',
    author: 'Marco De Luca',
    role: 'CFO',
    company: 'Helix Biotech',
    stat: '+2.4× IRR',
    stat_label: 'Post-transformation',
  },
  {
    quote: 'We went from 14 tools to one. Finance, procurement, deals and data rooms now share the same object graph.',
    author: 'Priya Nair',
    role: 'CIO',
    company: 'Meridian Retail Group',
    stat: '−62%',
    stat_label: 'Tool spend',
  },
];

export default function UseCasesPage() {
  const [caseStudies, setCaseStudies] = useState(DEFAULT_CASE_STUDIES);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await cmsApi.listPublic('case_study');
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setCaseStudies(data.map((i) => i.data || {}));
        }
      } catch { /* keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <PublicPageShell
      testId="use-cases-page"
      eyebrow="Use cases"
      title="Run every kind of deal on one unified lounge."
      subtitle="From M&A to carve-outs, fundraises, procurement, GRC and board packs — BIL maps to the way you actually work."
      ctaPrimary={{ label: 'See pricing', to: '/pricing' }}
      ctaSecondary={{ label: 'Start free', to: '/register' }}
    >
      {/* Use-case grid */}
      <section className="py-16 lg:py-20 bg-background">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="use-cases-grid">
            {USE_CASES.map((u) => {
              const Icon = u.icon;
              return (
                <div
                  key={u.key}
                  className="bg-card border border-border rounded-sm p-6 hover:shadow-lg hover:border-foreground/30 transition-all flex flex-col"
                  data-testid={`use-case-${u.key}`}
                >
                  <div
                    className="h-11 w-11 rounded-sm flex items-center justify-center mb-4"
                    style={{ background: `${u.tint}1A` }}
                  >
                    <Icon size={22} weight="duotone" color={u.tint} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold tracking-tight">
                    {u.label}
                  </h3>
                  <p className="font-medium text-sm mt-2 leading-snug" style={{ color: u.tint }}>
                    {u.hook}
                  </p>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                    {u.description}
                  </p>
                  <ul className="mt-4 space-y-2 flex-1">
                    {u.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13px]">
                        <CheckCircle size={14} weight="fill" className="flex-shrink-0 mt-0.5" style={{ color: u.tint }} />
                        <span className="text-foreground/90 leading-snug">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/register"
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold hover:gap-2.5 transition-all"
                    style={{ color: u.tint }}
                    data-testid={`use-case-cta-${u.key}`}
                  >
                    Try it free <ArrowRight size={14} weight="bold" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Customer stories */}
      <section className="py-16 lg:py-20 bg-[#f5f6fa] dark:bg-muted/20 border-y border-border">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#002677] mb-3">
              Customer stories
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
              Measurable outcomes, not just dashboards.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="case-studies">
            {caseStudies.map((cs, i) => (
              <div
                key={i}
                className="bg-card border border-border rounded-sm p-6 flex flex-col"
                data-testid={`case-study-${i}`}
              >
                <Quotes size={26} weight="fill" className="text-[#002677]/30 mb-3" />
                <p className="text-sm text-foreground leading-relaxed flex-1">{cs.quote}</p>
                <div className="mt-5 pt-5 border-t border-border flex items-end justify-between">
                  <div>
                    <p className="text-xs tracking-[0.18em] uppercase font-semibold text-muted-foreground">
                      {cs.stat_label || cs.statLabel}
                    </p>
                    <p className="font-heading text-2xl font-bold tracking-tight text-[#002677] mt-1">
                      {cs.stat}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sm">{cs.author}</p>
                    <p className="text-xs text-muted-foreground">{cs.role}</p>
                    <p className="text-xs text-muted-foreground">{cs.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / compliance strip */}
      <section className="py-12 bg-background">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="bg-card border border-border rounded-sm p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-sm bg-[#002677]/10 flex items-center justify-center flex-shrink-0">
                <Shield size={22} weight="duotone" className="text-[#002677]" />
              </div>
              <div>
                <p className="font-semibold text-sm">Bank-grade security</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  SOC 2 Type II · ISO 27001 · GDPR · HIPAA BAA · SSO/SAML · EU data residency
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Star size={16} weight="fill" className="text-[#FFD100]" />
              <span className="text-sm font-medium">4.9 / 5 · 800+ reviews</span>
            </div>
          </div>
        </div>
      </section>
    </PublicPageShell>
  );
}
